// Hover-value formatting for map layers (Front-End.md §A7): raw pixel/feature values ->
// human text using the layer's render metadata. Pure + unit-tested.
import type { ManifestLayer } from "../types/contract";
import { postJsonForFile } from "../api/httpGateway";
import {
  analyteOf,
  convertSoilValue,
  lbsPerPpmAt,
  STORED_DEPTH_IN,
  unitSuffix,
} from "./soilUnits";
import { depthSuffix, isBufferPhKey } from "./depthDisplay";
import { soilUnits } from "../stores/soilUnitsStore";
// null = nothing to show (nodata / NaN / outside the image) PURE: no lens inside it.
export function formatLayerValue(
  layer: ManifestLayer,
  v: number | null | undefined,
): string | null {
  if (v == null || Number.isNaN(v)) return null;
  if (layer.nodata !== undefined && v === layer.nodata) return null;
  if (layer.categories && layer.categories.length > 0) {
    const entry = layer.categories.find((c) => c.class_value === Math.round(v));
    return entry ? entry.label : `class ${Math.round(v)}`;
  }
  const abs = Math.abs(v);
  const text =
    abs >= 100 ? v.toFixed(0) : abs >= 10 ? v.toFixed(1) : v.toFixed(2);
  return layer.unit ? `${text} ${layer.unit}` : text;
}

/*
 *
 * True when NEITHER lens would move this layer's numbers.
 *
 * Exists so the legend can render an untouched tick byte-for-byte as it always did: reformatting `10` into
 * `10.00` on every layer the lenses don't touch is churn, and it was the original tell that the legend and
 * the readout had diverged. Asking here keeps that decision on the same code path as the conversion itself.
 */
export function lensIsIdentity(
  layer: ManifestLayer,
  opts: { atDepth: boolean },
): boolean {
  if (opts.atDepth) return false;
  if (massConvertible(layer) && soilUnits().massBasis === "lbs_ac")
    return false;
  const an = analyteOf(layer.id ?? layer.title);
  if (an === "other") return true;
  const c = convertSoilValue(1, an, soilUnits());
  return c.value === 1 && unitSuffix(c) === "";
}

/*
 *
 * The extractant lens covers P/K/Ca/Mg (the analytes with published method factors); ppm→lbs is
 * pure physics and applies to EVERY concentration — S, Zn, Mn, B included — while never touching pH
 * (unitless) OM (%) or CEC (meq/100g).
 */
export function massConvertible(layer: { unit?: string }): boolean {
  return layer.unit === "ppm";
}

/* * The unit a layer READS in under the current lens — for the legend header, which prints it standalone. */
export function displayUnitOf(layer: ManifestLayer): string | undefined {
  return massConvertible(layer) && soilUnits().massBasis === "lbs_ac"
    ? "lbs/ac"
    : layer.unit;
}

/*
 *
 * ppm → the display mass basis, at the DISPLAY depth. Identity when the preference is ppm.
 *
 * Depth-aware on purpose: 2.4 lb/ac per ppm is the 8" furrow slice, and the slice's mass scales linearly
 * with depth — "lbs/acre at 6-inch sampling" is the mass of a 6-inch slice, so a value being displayed at
 * 6" converts by 1.8, not 2.4. A fixed factor would overstate every shallow reading by depth/8.
 */
export function toMassBasis(
  v: number,
  layer: { unit?: string },
  depthIn: number,
): number {
  return massConvertible(layer) && soilUnits().massBasis === "lbs_ac"
    ? v * lbsPerPpmAt(depthIn)
    : v;
}

/*
 *
 * THE display pipeline for a soil number — the ONLY place either lens is applied.
 *
 * Order matters and mirrors ingestion run backwards. Ingest goes measured -> M3-equivalent -> depth-norm
 * -> stored M3 @ 8". Coming back out we undo that in reverse: the DEPTH solve happens first, on the raw
 * M3 value the coefficients were fitted on (that is `v`, already solved by the caller), and only then does
 * the EXTRACTANT multiplier land. Converting first would feed compute's model a Bray number it was never
 * fitted on.
 *
 * `atDepth` says whether `v` is a real at-depth estimate. It drives the label, so a value that could not be
 * re-read is never presented as one that was.
 */
export interface LensedValue {
  /* * the number to show, after the extractant multiplier */
  value: number;
  /* * which extractant it is now in, or null when the analyte has none */
  extractant: string | null;
  /* * true when no published factor was available and the STORED value is being shown */
  fellBack: boolean;
}

/*
 *
 * THE MATH half of the pipeline: the extractant multiplier, applied to an already depth-solved value.
 *
 * Split out from `lensedLayerValue` so a caller that needs a BARE number can reuse the identical conversion
 * without inheriting the labels. Sample point labels are the case: forty points each reading "22.2 (Bray)"
 * is unreadable on a map, and the basis belongs in the layer title instead. Splitting formatting from math
 * keeps that from becoming a second copy of the conversion — which is precisely how the two lenses drifted
 * apart the first time.
 */
/* * Buffer pH has no extractant and no depth model. The stored number is the reading. */
export function layerSkipsSoilLens(layer: {
  id?: string | null;
  title?: string | null;
}): boolean {
  return isBufferPhKey(layer.id) || isBufferPhKey(layer.title);
}

export function lensedValue(
  layer: ManifestLayer,
  v: number,
  atDepth = true,
): LensedValue {
  if (layerSkipsSoilLens(layer))
    return { value: v, extractant: null, fellBack: false };
  // the slice factor converts a value ON ITS OWN BASIS: an at-depth number is a depthIn-inch slice
  // an unconverted number is the stored 8″ slice — stored ppm × the 6″ factor was neither.
  // `atDepth` = the caller's promise about `v` (sample labels: solved; CA stars: stored).
  const basisDepth = atDepth ? soilUnits().depthIn : STORED_DEPTH_IN;
  // an UNLENSED sample layer's stored numbers are NOT Mehlich-3 — the method factor must not touch them
  if (layer.unlensed)
    return {
      value: toMassBasis(v, layer, basisDepth),
      extractant: null,
      fellBack: true,
    };
  const c = convertSoilValue(
    v,
    analyteOf(layer.id ?? layer.title),
    soilUnits(),
  );
  // mass basis LAST: extractant factors are ppm-to-ppm (method conversions), so the slice multiplier must
  // land after them.
  return {
    value: toMassBasis(c.value, layer, basisDepth),
    extractant: c.extractant,
    fellBack: c.fellBack,
  };
}

export function lensedLayerValue(
  layer: ManifestLayer,
  v: number | null | undefined,
  opts: { depthIn: number; atDepth: boolean; withUnit?: boolean },
): string | null {
  if (v == null || Number.isNaN(v)) return null;
  if (layer.nodata !== undefined && v === layer.nodata) return null;
  if (layer.categories && layer.categories.length > 0)
    return formatLayerValue(layer, v);
  if (layerSkipsSoilLens(layer)) {
    const unitLayer =
      opts.withUnit === false ? { ...layer, unit: undefined } : layer;
    return formatLayerValue(unitLayer, v);
  }
  const an = analyteOf(layer.id ?? layer.title);
  // unlensed layers (stored basis, extractant unknown) skip the method factor — see contract.ts
  const c = layer.unlensed
    ? { value: v, extractant: null, fellBack: true }
    : convertSoilValue(v, an, soilUnits());
  // slice factor on the value's OWN basis depth: atDepth values are a depthIn-inch slice
  // unconverted values are the stored 8″ slice, whatever the picker says.
  const shown = toMassBasis(
    c.value,
    layer,
    opts.atDepth ? opts.depthIn : STORED_DEPTH_IN,
  );
  // The legend prints its unit in the header, so a tick that repeats it reads as noise. Everything else
  // (hover lines, pin callouts) stands alone and needs it — in the DISPLAY unit, which the mass lens swaps.
  const unitLayer =
    opts.withUnit === false
      ? { ...layer, unit: undefined }
      : { ...layer, unit: displayUnitOf(layer) };
  const base = formatLayerValue(unitLayer, shown);
  if (base === null) return null;
  return `${base}${unitSuffix(c)}${an === "other" ? "" : depthSuffix(opts.depthIn, opts.atDepth)}`;
}

// File name the download button shows. The file itself is written on the server.
export function downloadFilenameFor(layer: ManifestLayer): string {
  const base =
    (layer.title ?? layer.id ?? "layer")
      .replace(/[^\w.-]+/g, "_")
      .replace(/^_+|_+$/g, "") || "layer";
  const isVector = layer.kind === "geojson" || layer.kind === "vector";
  return `${base}.${isVector ? "geojson" : "tif"}`;
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function filenameOf(header: string | null, fallback: string): string {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(header ?? "");
  if (encoded?.[1]) return decodeURIComponent(encoded[1]);
  const plain = /filename="([^"]+)"/i.exec(header ?? "");
  return plain?.[1] || fallback;
}

// Inline GeoJSON is written on the server. A COG is a file that already exists,
// fetched so the browser can name it (a cross-origin link ignores download).
export async function downloadLayer(layer: ManifestLayer): Promise<void> {
  const name = downloadFilenameFor(layer);
  if (layer.inline) {
    const { blob, disposition } = await postJsonForFile("/exports/geojson", {
      filename: name,
      document: layer.inline,
    });
    return saveBlob(blob, filenameOf(disposition, name));
  }
  if (layer.signed_url) {
    const res = await fetch(layer.signed_url);
    if (!res.ok) throw new Error(`download failed (${res.status})`);
    return saveBlob(await res.blob(), name);
  }
  throw new Error("This layer has no downloadable source.");
}

// filename-safe label (keep letters/digits across scripts + a few punctuation chars like the +/- in "wk +6").
export function safeFileName(s: string): string {
  return (
    (s || "layer")
      .replace(/[^\p{L}\p{N}._+-]+/gu, "_")
      .replace(/^_+|_+$/g, "") || "layer"
  );
}

export async function downloadBoundary(
  fieldName: string,
  layer: ManifestLayer,
): Promise<void> {
  let geo: unknown = layer.inline;
  if (!geo && layer.signed_url) {
    const res = await fetch(layer.signed_url);
    if (!res.ok) throw new Error(`download failed (${res.status})`);
    geo = await res.json();
  }
  if (!geo || typeof geo !== "object")
    throw new Error("The field boundary isn’t available to download yet.");
  const { blob, disposition } = await postJsonForFile("/exports/boundary", {
    field_name: fieldName,
    geojson: geo,
  });
  saveBlob(blob, filenameOf(disposition, "boundary.geojson"));
}
